import {
  createBrowserRouter,
  RouteObject,
  RouterProvider,
  useLocation,
} from "react-router";
import { TypographyPlayground } from "@components/playgrounds/TypographyPlayground";
import { ThemingPlayground } from "@components/playgrounds/ThemingPlayground";
import { GridPlayground } from "@components/playgrounds/GridPlayground";
import { AnimatedSplitLinesPlayground } from "@components/playgrounds/AnimatedSplitLinesPlayground";
import { BodyFeaturePlayground } from "@components/playgrounds/BodyFeaturePlayground";
import { MediaGalleryPlayground } from "@components/playgrounds/MediaGalleryPlayground";
import { NumberAnimationPlayground } from "@components/playgrounds/NumberAnimationPlayground";
import { ArticlePageProviders } from "./ArticlePageProviders";
import { CaseStudyPageProviders } from "./CaseStudyPageProviders";
import { EventPageProviders } from "./EventPageProviders";
import { StandardPageProviders } from "./StandardPageProviders";
import { useEffect, useMemo } from "react";
import { NotFound } from "./NotFound";
import { Analytics } from "@utils/Analytics";
import { Forward } from "./Forward";
import { useGlobalContext } from "@utils/GlobalContext";

export const Router = () => {
  const { supportedLocales } = useGlobalContext();

  const router = useMemo(
    () => createRouter(supportedLocales),
    [supportedLocales]
  );

  useEffect(() => {
    // Listen for route changes only
    const unsubscribe = router.subscribe((state) => {
      if (state.navigation.state === "idle") {
        Analytics.trackPageView();
      }
    });

    return () => unsubscribe();
  }, [router]);

  return <RouterProvider router={router} />;
};

const createRouter = (supportedLocales: Array<string>) =>
  createBrowserRouter([
    ...supportedLocales
      .map((l) => l.toLocaleLowerCase())
      .map((localeCode) => ({
        path: localeCode,
        children: [
          ...pagesRoutes,
          {
            path: "*",
            Component: NotFound,
          },
        ],
      })),
    {
      path: "*",
      Component: ForwardToLocaleRoute,
    },

    ...playgroundTestRoutes,
  ]);

const ForwardToLocaleRoute = () => {
  const location = useLocation();

  return (
    <Forward
      replace
      to={`${location.pathname}${location.search}${location.hash}`}
    />
  );
};

const StillWatchingRedirect = () => {
  return <Forward to="/still-watching" replace />;
};

const createStillWatchingRedirects = () => {
  const variations = ['stillwatching', 'StillWatching', 'stillWatching'];
  return variations.map(variation => ({
    path: variation,
    Component: StillWatchingRedirect,
  }));
};

const pagesRoutes: Array<RouteObject> = [
  {
    index: true,
    Component: StandardPageProviders,
  },
  {
    path: "article",
    children: [{ path: ":slug", Component: ArticlePageProviders }],
  },
  {
    path: "case-studies/:slug",
    Component: CaseStudyPageProviders,
  },
  {
    path: "event/:slug",
    Component: EventPageProviders,
  },
  ...createStillWatchingRedirects(),
  {
    path: ":slug",
    Component: StandardPageProviders,
  },
];

const playgroundTestRoutes = import.meta.env.DEV
  ? [
      {
        path: "playground",
        children: [
          { path: "typography", Component: TypographyPlayground },
          { path: "theming", Component: ThemingPlayground },
          { path: "grid", Component: GridPlayground },
          {
            path: "animated-split-lines",
            Component: AnimatedSplitLinesPlayground,
          },
          { path: "feature-text", Component: BodyFeaturePlayground },
          { path: "media-gallery", Component: MediaGalleryPlayground },
          {
            path: "number-animation",
            Component: NumberAnimationPlayground,
          },
        ],
      },
    ]
  : [];
